import { AnalyticsAdapter, EventBase, UserVars } from '@lifeway/frontend-analytics';
import apiClient from '../../../services/apiClient';
import { logger } from '../../../utils/logger';
import { trackActiveCampaignUser } from '../api/trackActiveCampaignUser';
import { isActiveCampaignEnabled } from '../utils/isActiveCampaignEnabled';

type VisitorGlobal = { (...args: unknown[]): void; q?: unknown[]; l?: number };

declare global {
  interface Window {
    visitorGlobalObjectAlias?: string;
    vgo?: VisitorGlobal;
  }
}

const VISITOR_SCRIPT_SRC = 'https://diffuser-cdn.app-us1.com/diffuser/diffuser.js';
const VISITOR_ACCOUNT_ID = '475317757';

function isVisitorTrackingEnabled(): boolean {
  const origin = window.getOrigin ? window.getOrigin() : 'GRID';
  return !['CONNEXUS'].includes(origin);
}

function initVisitorTracker(): void {
  if (!isVisitorTrackingEnabled() || window.location.host.includes('local') || window.vgo) {
    return;
  }

  // The diffuser script finds its own global through the alias, and drains the queue
  // and start time the stub records while the script is still loading.
  window.visitorGlobalObjectAlias = 'vgo';
  const vgo: VisitorGlobal = function () {
    // eslint-disable-next-line prefer-rest-params
    (vgo.q = vgo.q || []).push(arguments);
  };
  vgo.l = new Date().getTime();
  window.vgo = vgo;

  const script = document.createElement('script');
  script.async = true;
  script.src = VISITOR_SCRIPT_SRC;
  document.head.appendChild(script);

  vgo('setAccount', VISITOR_ACCOUNT_ID);
  vgo('setTrackByDefault', true);
  vgo('process');
}

function trackEvent(eventName: string, email: string) {
  const data = { event: {}, eventName, email };
  logger.debug('ActiveCampaign.trackEvent called with', data);

  if (isActiveCampaignEnabled()) {
    return apiClient.post('/trackingAC', data);
  }
  return Promise.resolve();
}

export class ActiveCampaignAdapter implements AnalyticsAdapter {
  constructor() {
    initVisitorTracker();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public emit(...events: EventBase<any, any, any>[]): Promise<unknown> {
    return Promise.allSettled(
      events
        .map(event => {
          switch (event.eventName) {
            case 'Purchase Initiated': {
              return trackEvent(`Carted_${event.context.itemNumber}`, event.context.userEmail);
            }
            default:
              return;
          }
        })
        .filter(Boolean)
    );
  }

  public log(): void {}
  public unloadPage(): void {}
  public updatePage(): void {}
  public viewPage(): void {}
  public anonymize(): Promise<void> {
    return Promise.resolve();
  }
  public identify(userVars: UserVars): Promise<void> {
    if (!isActiveCampaignEnabled() || typeof userVars?.email !== 'string') {
      return Promise.resolve();
    }

    return trackActiveCampaignUser({
      email: userVars.email,
      currentOrgId: String(userVars.currentOrgId ?? ''),
      currentOrgName: String(userVars.currentOrgName ?? ''),
      currentOrgRoles: Array.isArray(userVars.currentOrgRoles) ? (userVars.currentOrgRoles as string[]) : [],
      allRoles: Array.isArray(userVars.allRoles) ? (userVars.allRoles as string[]) : [],
      orgSub: Boolean(userVars.orgSub),
      curriculumSub: Boolean(userVars.curriculumSub),
      personalSub: Boolean(userVars.personalSub),
    })
      .then(() => {})
      .catch(error => {
        logger.error('Failed to track ActiveCampaign user', error);
      });
  }
  public addSessionContext(_newContext: Record<string, unknown>): void {}
}
